"use client";

import { useEffect, useId, useRef, useState } from "react";
import type { GuideSearchRow } from "@/lib/virtualspace/guide/guide-search";

/**
 * The header's search: a plain form that lands on the search page (works
 * without scripting), and, once mounted, suggestions from the search API as
 * the visitor types (debounced, at most eight, arrow keys and Enter).
 */
export function GuideSearchBox({
  action,
  lang,
  copy,
  compact = false,
  initial = "",
}: {
  /** The search page, e.g. `/egypt/search`. */
  action: string;
  lang: "en" | "ar";
  copy: { label: string; placeholder: string; go: string; suggestions: string };
  compact?: boolean;
  initial?: string;
}) {
  const [q, setQ] = useState(initial);
  const [rows, setRows] = useState<GuideSearchRow[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const listId = useId();
  const timer = useRef<number | null>(null);
  const abort = useRef<AbortController | null>(null);

  useEffect(() => {
    if (timer.current) window.clearTimeout(timer.current);
    const term = q.trim();
    if (term.length < 2) {
      setRows([]);
      return;
    }
    timer.current = window.setTimeout(async () => {
      abort.current?.abort();
      const controller = new AbortController();
      abort.current = controller;
      try {
        const res = await fetch(`/api/localized/search?q=${encodeURIComponent(term)}&lang=${lang}`, { signal: controller.signal });
        if (!res.ok) return;
        const data = (await res.json()) as { rows: GuideSearchRow[] };
        setRows(data.rows ?? []);
        setOpen(true);
        setActive(-1);
      } catch {
        // Typing continues; the form still submits.
      }
    }, 180);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [q, lang]);

  const choose = (row: GuideSearchRow) => {
    if (row.href) window.location.assign(row.href);
  };

  return (
    <form
      role="search"
      action={action}
      method="get"
      className={`eg-search${compact ? " eg-search--compact" : ""}`}
      onSubmit={() => setOpen(false)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      {lang === "ar" ? <input type="hidden" name="lang" value="ar" /> : null}
      <input
        type="search"
        name="q"
        value={q}
        onChange={(event) => setQ(event.target.value)}
        onFocus={() => rows.length > 0 && setOpen(true)}
        onKeyDown={(event) => {
          if (!open || rows.length === 0) return;
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setActive((i) => (i + 1) % rows.length);
          } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActive((i) => (i - 1 + rows.length) % rows.length);
          } else if (event.key === "Enter" && active >= 0) {
            event.preventDefault();
            choose(rows[active]);
          } else if (event.key === "Escape") setOpen(false);
        }}
        placeholder={copy.placeholder}
        aria-label={copy.label}
        aria-autocomplete="list"
        aria-controls={listId}
        aria-expanded={open && rows.length > 0}
        autoComplete="off"
        minLength={2}
        required
        className="eg-search__input"
      />
      <button type="submit" className="eg-search__go" aria-label={copy.go}>
        <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" strokeLinecap="round" />
        </svg>
      </button>
      {open && rows.length > 0 ? (
        <ul id={listId} role="listbox" aria-label={copy.suggestions} className="eg-search__list">
          {rows.map((row, i) => (
            <li key={row.key} role="option" aria-selected={i === active}>
              <a
                href={row.href ?? "#"}
                {...(row.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                className={`eg-search__row${i === active ? " eg-search__row--active" : ""}`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => {
                  if (!row.href) event.preventDefault();
                }}
              >
                <span aria-hidden="true" className={`eg-layer-key eg-layer-key--${row.kind === "venue" ? "places" : row.kind === "event" ? "events" : "news"}`} />
                <span className="eg-search__title">{row.title}</span>
                {row.meta ? <span className="eg-search__meta">{row.meta}</span> : null}
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </form>
  );
}
